import React, { useState } from 'react'
import {
  useSchedulesByBatch,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
} from '@/hooks/useSchedules'
import { ScheduleForm, type ScheduleFormData } from '@/components/batches/ScheduleForm'

interface Props {
  batchId: string
}

export const AdminBatchSchedulesTab: React.FC<Props> = ({ batchId }) => {
  const { data: schedulesResponse, isLoading, error } = useSchedulesByBatch(batchId)
  const createSchedule = useCreateSchedule(batchId)
  const updateSchedule = useUpdateSchedule(batchId)
  const deleteSchedule = useDeleteSchedule(batchId)

  const [showForm, setShowForm] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<any | null>(null)

  const schedules = schedulesResponse?.data || []

  const handleOpenForm = (schedule?: any) => {
    if (schedule) {
      setEditingSchedule({
        id: schedule.id,
        sessionNumber: schedule.sessionNumber ?? schedule.session_number,
        title: schedule.title,
        description: schedule.description || '',
        dateTime: (schedule.dateTime || schedule.date_time) ? new Date(schedule.dateTime || schedule.date_time).toISOString().slice(0, 16) : '',
        durationMinutes: schedule.durationMinutes ?? schedule.duration_minutes ?? 120,
        location: schedule.location || '',
        meetingLink: schedule.meetingLink ?? schedule.meeting_link ?? '',
        type: schedule.type || 'practical',
      })
    } else {
      setEditingSchedule(null)
    }
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingSchedule(null)
  }

  const handleSubmit = (data: ScheduleFormData) => {
    const payload = {
      sessionNumber: data.sessionNumber,
      title: data.title,
      description: data.description || undefined,
      dateTime: data.dateTime ? new Date(data.dateTime).toISOString() : '',
      durationMinutes: data.durationMinutes,
      location: data.location || undefined,
      meetingLink: data.meetingLink || undefined,
      type: data.type,
      isAttendanceRequired: true,
    }

    if (editingSchedule) {
      updateSchedule.mutate(
        { id: editingSchedule.id, data: payload },
        {
          onSuccess: () => handleCloseForm(),
          onError: (err: any) => alert(err.message || 'Gagal memperbarui sesi'),
        }
      )
    } else {
      createSchedule.mutate(payload, {
        onSuccess: () => handleCloseForm(),
        onError: (err: any) => alert(err.message || 'Gagal menambah sesi'),
      })
    }
  }

  const handleDelete = (id: string, title: string) => {
    if (window.confirm(`Hapus sesi "${title}"?`)) {
      deleteSchedule.mutate(id, {
        onError: (err: any) => alert(err.message || 'Gagal menghapus sesi'),
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 space-y-4">
        <div className="size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        <p className="text-sm font-bold text-slate-400 tracking-tight">Memuat jadwal sesi...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-500 flex items-center gap-3">
        <span className="material-symbols-outlined text-[20px]">error</span>
        <span className="text-sm font-bold">{(error as Error).message}</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between bg-white dark:bg-slate-900/50 p-6 rounded-[2rem] border border-slate-100 dark:border-white/5 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Jadwal Sesi Pertemuan</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Kelola sesi pelatihan untuk batch ini.</p>
        </div>
        <button
          onClick={() => handleOpenForm()}
          className="flex items-center gap-2 px-6 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-bold text-sm hover:shadow-xl transition-all"
        >
          <span className="material-symbols-outlined text-[20px]">add</span>
          Tambah Sesi
        </button>
      </div>

      {schedules.length === 0 ? (
        <div className="text-center py-32 bg-white dark:bg-slate-900 rounded-[3rem] border border-dashed border-slate-200 dark:border-white/10">
          <div className="size-20 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
            <span className="material-symbols-outlined text-4xl">event_busy</span>
          </div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Belum Ada Sesi</h3>
          <p className="text-sm text-slate-500 font-medium max-w-sm mx-auto leading-relaxed">
            Batch ini belum memiliki jadwal pertemuan. Klik tombol Tambah Sesi untuk memulai.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {schedules.map((schedule: any) => (
            <div key={schedule.id} className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-white/5 p-6 hover:shadow-2xl hover:shadow-slate-200/40 transition-all duration-300 flex flex-col group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 transition-all group-hover:scale-150" />
              
              <div className="flex items-start justify-between mb-4 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="size-10 bg-primary/10 rounded-xl text-primary font-black flex items-center justify-center">
                    S{schedule.sessionNumber ?? schedule.session_number}
                  </div>
                  <div className="px-2.5 py-1 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 text-[10px] font-black uppercase tracking-wider rounded-lg">
                    {schedule.type === 'theory' ? 'Teori' : 'Praktik'}
                  </div>
                </div>
                
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleOpenForm(schedule)} className="size-8 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-primary transition-all">
                    <span className="material-symbols-outlined text-[18px]">edit</span>
                  </button>
                  <button onClick={() => handleDelete(schedule.id, schedule.title)} className="size-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-500 transition-all">
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  </button>
                </div>
              </div>

              <div className="mb-6 flex-1 relative z-10">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1 line-clamp-2">
                  {schedule.title}
                </h3>
                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                  {schedule.description || 'Tanpa deskripsi'}
                </p>
              </div>

              <div className="space-y-3 pt-4 border-t border-slate-50 dark:border-white/5 relative z-10">
                <div className="flex items-center gap-3 text-sm">
                  <span className="material-symbols-outlined text-[18px] text-slate-400">schedule</span>
                  <div className="flex flex-col">
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                      {(schedule.dateTime || schedule.date_time) ? new Date(schedule.dateTime || schedule.date_time).toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'TBD'}
                    </span>
                    <span className="text-[11px] text-slate-500 font-medium">
                      {(schedule.dateTime || schedule.date_time) ? new Date(schedule.dateTime || schedule.date_time).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) : ''} • {schedule.durationMinutes ?? schedule.duration_minutes ?? 120} mnt
                    </span>
                  </div>
                </div>
                {(schedule.location || schedule.meetingLink || schedule.meeting_link) && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="material-symbols-outlined text-[18px] text-slate-400">
                      {schedule.location ? 'location_on' : 'videocam'}
                    </span>
                    <span className="font-bold text-slate-700 dark:text-slate-200 truncate">
                      {schedule.location || 'Online / Meeting Link'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[100] p-6" onClick={handleCloseForm}>
          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-10 w-full max-w-2xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)] border border-white/20 max-h-[90vh] overflow-y-auto custom-scrollbar" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="size-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary text-[28px]">{editingSchedule ? 'edit_calendar' : 'event_available'}</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                    {editingSchedule ? 'Edit Jadwal Sesi' : 'Tambah Sesi Baru'}
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1">
                    Konfigurasi jadwal pertemuan untuk batch ini
                  </p>
                </div>
              </div>
              <button onClick={handleCloseForm} className="size-10 rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 flex items-center justify-center transition-all text-slate-400">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <ScheduleForm
              initialData={editingSchedule || undefined}
              onSubmit={handleSubmit}
              onCancel={handleCloseForm}
              isLoading={createSchedule.isPending || updateSchedule.isPending}
            />
          </div>
        </div>
      )}
    </div>
  )
}
